import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSponsorServices } from '../../apis/sponsorService';
import { toast } from 'react-toastify';
import { Save, X, ArrowLeft, Image } from 'lucide-react';

const EditSponsorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getSponsorById, updateSponsor, loading } = useSponsorServices();

  const [formData, setFormData] = useState({
    name: '',
    websiteUrl: '',
    description: '',
    isActive: true,
    displayOrder: 0,
    sponsorDayDate: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSponsorData();
  }, [id]);

  const fetchSponsorData = async () => {
    try {
      const response = await getSponsorById(id);
      if (response.success) {
        const sponsor = response.data;
        setFormData({
          name: sponsor.name || '',
          websiteUrl: sponsor.websiteUrl || '',
          description: sponsor.description || '',
          isActive: sponsor.isActive !== undefined ? sponsor.isActive : true,
          displayOrder: sponsor.displayOrder || 0,
          sponsorDayDate: sponsor.sponsorDayDate ? new Date(sponsor.sponsorDayDate).toISOString().slice(0, 10) : ''
        });
        if (sponsor.logoUrl) {
          setLogoPreview(sponsor.logoUrl);
        }
      } else {
        toast.error('Failed to fetch sponsor data');
        navigate('/super-admin/sponsor-list');
      }
    } catch (err) {
      toast.error('An error occurred while fetching sponsor data');
      navigate('/super-admin/sponsor-list');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image file size should be less than 2MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const sponsorData = {
        ...formData,
        sponsorDayDate: formData.sponsorDayDate ? new Date(formData.sponsorDayDate).toISOString() : null,
        displayOrder: parseInt(formData.displayOrder) || 0
      };

      const response = await updateSponsor(id, sponsorData, logoFile);
      if (response.success) {
        toast.success('Sponsor updated successfully');
        navigate('/super-admin/sponsor-list');
      } else {
        toast.error(response.message || 'Failed to update sponsor');
      }
    } catch (err) {
      toast.error('An error occurred while updating the sponsor');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/super-admin/sponsor-list')}
          className="mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Edit Sponsor</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sponsor Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full p-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
            <input
              type="url"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              placeholder="https://example.com"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
            <input
              type="number"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sponsor Day Date</label>
            <input
              type="date"
              name="sponsorDayDate"
              value={formData.sponsorDayDate}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
            {logoPreview ? (
              <div className="flex items-start space-x-4">
                <img src={logoPreview} alt="Logo preview" className="w-32 h-32 object-contain rounded-lg border" />
                <button type="button" onClick={removeLogo} className="p-2 bg-red-500 text-white rounded">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md cursor-pointer">
                <Image size={16} className="mr-2" />
                Upload Logo
                <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
              </label>
            )}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="h-4 w-4 text-green-600"
            />
            <label className="ml-2 text-sm text-gray-700">Active</label>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/super-admin/sponsor-list')}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
          >
            <Save size={18} className="mr-2" />
            {loading ? 'Updating...' : 'Update Sponsor'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditSponsorPage;

